import React, { useEffect, useState } from 'react';
import { Modal, Table, Typography, Tag, Button } from 'antd';
import api from '../api';

const { Text } = Typography;

function fmtTime(s) {
  if (s == null) return '-';
  const n = Math.round(Number(s));
  if (n === 0) return '0:00';
  const m = Math.floor(n / 60);
  const sec = n % 60;
  return `${m}:${String(sec).padStart(2, '0')}`;
}

// Met means at or under the goal. These are handle times, so lower is better,
// and Target is what "the goal" means on the rest of the app.
const met = (v, goal) => v != null && goal != null && Number(v) <= Number(goal);

function MetricCell({ value, monthVal, isTime, goal }) {
  const display = isTime ? fmtTime(value) : (value == null ? '-' : Number(value));
  const chip    = monthVal != null
    ? (isTime ? fmtTime(monthVal) : Number(monthVal))
    : null;
  const hitToday = met(value, goal);
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <Text style={{
        fontFamily: 'monospace', fontSize: 13, fontWeight: 500,
        // Green only where there is a goal to have beaten. Without one the
        // number is uncoloured rather than neutral-looking-good.
        color: goal != null ? (hitToday ? '#389e0d' : undefined) : undefined,
      }}>{display}</Text>
      {chip != null && (
        <Tag style={{ fontSize: 10, margin: 0, width: 'fit-content', background: '#f0f4f8', border: 'none', color: '#6b829e', fontFamily: 'monospace' }}>
          {chip} your monthly avg
        </Tag>
      )}
      {goal != null && (
        <Tag style={{ fontSize: 10, margin: 0, width: 'fit-content', background: 'transparent', border: 'none', color: '#8c8c8c', fontFamily: 'monospace', padding: 0 }}>
          goal {isTime ? fmtTime(goal) : Number(goal)}
        </Tag>
      )}
    </div>
  );
}

const COLUMNS = [
  { title: 'Campaign', dataIndex: 'campaign', key: 'campaign', fixed: 'left', width: 110,
    render: (v) => <Text strong style={{ color: '#162d4e' }}>{v}</Text> },
  { title: 'Media', dataIndex: 'media', key: 'media', fixed: 'left', width: 110,
    render: (v) => <Text type="secondary" style={{ fontSize: 12 }}>{v}</Text> },
  { title: 'Contacts', dataIndex: 'contacts', key: 'contacts', width: 90, align: 'right',
    render: (v, r) => (
      <MetricCell value={v} monthVal={r.mo_contacts_per_day} isTime={false} />
    ),
  },
  { title: 'Avg Talk', dataIndex: 'avg_talk', key: 'avg_talk', width: 100, align: 'right',
    render: (v, r) => <MetricCell value={v} monthVal={r.mo_avg_talk} isTime goal={r.goals?.avg_talk} />,
  },
  { title: 'Avg ACW', dataIndex: 'avg_acw', key: 'avg_acw', width: 100, align: 'right',
    render: (v, r) => <MetricCell value={v} monthVal={r.mo_avg_acw} isTime goal={r.goals?.avg_acw} />,
  },
  { title: 'Last ACW', dataIndex: 'last_acw', key: 'last_acw', width: 88, align: 'right',
    render: (v) => <Text style={{ fontFamily: 'monospace', fontSize: 13 }}>{fmtTime(v)}</Text>,
  },
  { title: 'Avg HT', dataIndex: 'avg_ht', key: 'avg_ht', width: 100, align: 'right',
    render: (v, r) => <MetricCell value={v} monthVal={r.mo_avg_ht} isTime goal={r.goals?.avg_ht} />,
  },
  { title: 'Max Talk', dataIndex: 'max_talk', key: 'max_talk', width: 90, align: 'right',
    render: (v) => <Text style={{ fontFamily: 'monospace', fontSize: 13 }}>{fmtTime(v)}</Text>,
  },
  { title: 'Max ACW', dataIndex: 'max_acw', key: 'max_acw', width: 90, align: 'right',
    render: (v) => <Text style={{ fontFamily: 'monospace', fontSize: 13 }}>{fmtTime(v)}</Text>,
  },
  { title: 'Max HT', dataIndex: 'max_ht', key: 'max_ht', width: 90, align: 'right',
    render: (v) => <Text style={{ fontFamily: 'monospace', fontSize: 13 }}>{fmtTime(v)}</Text>,
  },
  { title: 'No Contact / Wrong Number', dataIndex: 'no_contact', key: 'no_contact', width: 170, align: 'right',
    render: (v, r) => <MetricCell value={v} monthVal={r.mo_no_contact} isTime={false} />,
  },
];

export default function DailyRecapModal({ currentUser }) {
  const [recapData, setRecapData] = useState(null);
  const [open,      setOpen]      = useState(false);

  useEffect(() => {
    // Agents only: it is a recap of their own calls. No user_id is sent --
    // for an agent the endpoint always answers about the caller, whatever is
    // passed, so there is nothing to choose and nothing to get wrong.
    if (!currentUser || currentUser.Role !== 'agent') return;

    api.get('/api/agent/daily-recap')
      .then(async (r) => {
        if (!r.data || !r.data.recap_date) return;

        // Fail open. The seen check only decides whether to show the recap
        // again today, so a failure there must not suppress it: when the
        // AgentDailyRecapSeen table was missing in production this endpoint
        // 500'd, the rejection propagated to the catch below, and the recap
        // silently never appeared for anyone.
        let seen = false;
        try {
          const seenRes = await api.get('/api/agent/daily-recap/seen', {
            params: { recap_date: r.data.recap_date },
          });
          seen = !!seenRes.data?.seen;
        } catch {
          seen = false;
        }

        if (!seen) {
          setRecapData(r.data);
          setOpen(true);
        }
      })
      .catch(() => {});
  }, [currentUser]);

  const handleClose = async () => {
    setOpen(false);
    if (recapData?.recap_date) {
      try {
        await api.post('/api/agent/daily-recap/seen', { recap_date: recapData.recap_date });
      } catch {}
    }
  };

  if (!open || !recapData) return null;

  const hour = new Date().getHours();
  const greeting = hour < 12 ? 'Morning' : hour < 17 ? 'Afternoon' : 'Evening';
  const firstName = recapData.first_name || 'there';
  const dateStr = new Date(recapData.recap_date + 'T12:00:00').toLocaleDateString('en-US', {
    weekday: 'long', month: 'long', day: 'numeric',
  });
  // The recap is the most recent day with calls, which is often not today --
  // a Monday recap is usually Friday's or Saturday's. So the banner names the
  // day rather than saying "today". Without the weekday: the header above
  // already carries it, and in the middle of a sentence it is one word too many.
  const dayStr = new Date(recapData.recap_date + 'T12:00:00').toLocaleDateString('en-US', {
    month: 'long', day: 'numeric',
  });

  return (
    <Modal maskClosable={false}
      open={open}
      onCancel={handleClose}
      footer={null}
      width={900}
      styles={{ body: { padding: 0 }, header: { display: 'none' } }}
      destroyOnClose
    >
      {/* Navy header */}
      <div style={{
        background: '#162d4e',
        padding: '20px 26px 16px',
        borderRadius: '8px 8px 0 0',
        borderLeft: '4px solid #e8682a',
      }}>
        <div style={{ fontSize: 20, fontWeight: 600, color: '#fff', letterSpacing: '-0.02em' }}>
          Good {greeting}, {firstName}!
        </div>
        <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.7)', marginTop: 4 }}>
          Quick breakdown of your recent activity on {dateStr}:
        </div>
      </div>

      {/* What they hit, said once. Per-cell praise on three metrics across
          several campaign rows would be a wall of green tags saying the same
          thing; one line naming the wins reads like someone noticed. */}
      {(() => {
        const LABELS = { avg_ht: 'AHT', avg_talk: 'Avg Talk', avg_acw: 'Avg ACW' };
        // Grouped by period, not by metric. Listing each metric with its own
        // period produces "AHT today and Avg ACW today and for the month",
        // where the two ands fight; this reads as one sentence.
        const today = [], month = [];
        for (const key of ['avg_ht', 'avg_talk', 'avg_acw']) {
          const rows = (recapData.stats || []).filter((r) => r.goals?.[key] != null);
          if (!rows.length) continue;
          // The month counts as well as the day: a bad day on a good month is
          // still a good month, and an agent should hear that.
          if (rows.some((r) => met(r[key], r.goals[key])))            today.push(LABELS[key]);
          if (rows.some((r) => met(r[`mo_${key}`], r.goals[key])))    month.push(LABELS[key]);
        }
        if (!today.length && !month.length) return null;
        const join = (a) => (a.length === 1 ? a[0]
          : `${a.slice(0, -1).join(', ')} and ${a.at(-1)}`);
        const clause = (a, when) => `your ${join(a)} goal${a.length > 1 ? 's' : ''} ${when}`;
        // The same metrics in both periods is the best case and the one most
        // worth saying plainly: naming them twice buries it in its own repeat.
        const same = today.length && today.length === month.length
          && today.every((m) => month.includes(m));
        const parts = [];
        if (same) {
          parts.push(clause(today, `on ${dayStr} and for the month`));
        } else {
          if (today.length) parts.push(clause(today, `on ${dayStr}`));
          if (month.length) parts.push(clause(month, 'for the month'));
        }
        const list = parts.join(', and ');
        return (
          <div style={{
            margin: '14px 20px 0',
            padding: '10px 14px',
            background: '#f6ffed',
            border: '1px solid #b7eb8f',
            borderRadius: 6,
            display: 'flex',
            alignItems: 'center',
            gap: 10,
          }}>
            <span style={{ fontSize: 18, lineHeight: 1 }} role="img" aria-label="celebration">🎉</span>
            <Text style={{ fontSize: 13, color: '#237804' }}>
              Nice work{firstName ? `, ${firstName}` : ''} — you hit {list}.
            </Text>
          </div>
        );
      })()}

      {/* Stats table */}
      <div style={{ overflowX: 'auto', padding: '0 0 4px' }}>
        <Table
          size="small"
          columns={COLUMNS}
          dataSource={(recapData.stats || []).map((r, i) => ({ ...r, key: i }))}
          pagination={false}
          scroll={{ x: 'max-content' }}
          bordered
        />
      </div>

      {/* Footer. The chips say "your monthly avg" on themselves, so a line
          explaining the phrase was repeating the label back at the reader. */}
      <div style={{
        padding: '12px 20px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-end',
        borderTop: '1px solid #f0f0f0',
      }}>
        <Button type="primary" style={{ background: '#e8682a', borderColor: '#e8682a' }} onClick={handleClose}>
          Close
        </Button>
      </div>
    </Modal>
  );
}
